import React, { useState, useEffect, useCallback, useRef } from "react";
import { CATEGORIES } from "../utils/categories";
import { MAX_OFFER_ID_DIGITS, expandScientific } from "../utils/couponCsv";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Spinner from "../components/Spinner";
import fetchData from "../api/fetchData";
import CouponModal from "../components/CouponModal";

// Trim + drop incomplete FAQ rows. Used both when loading a store (to set the
// baseline) and when saving, so the change-detection comparison is consistent.
const normalizeFaqs = (faqs) =>
  (Array.isArray(faqs) ? faqs : [])
    .map((f) => ({
      question: (f?.question || "").trim(),
      answer: (f?.answer || "").trim(),
    }))
    .filter((f) => f.question && f.answer);

// Build the store editing state from a fetched store, capturing `original*`
// baselines so updateStore only sends the fields that actually changed.
const buildStoreState = (data) => ({
  ...data,
  faqs: normalizeFaqs(data.faqs),
  aboutContent: data.aboutContent || "",
  category: data.category || "",
  originalName: data.name,
  originalCategory: data.category || "",
  originalDescription: data.description,
  originalAffiliateLink: data.affiliateLink,
  originalMetaKeywords: data.metaKeywords,
  originalMetaDescription: data.metaDescription,
  originalBrandLogoUrlSquare: data.brandLogoUrlSquare,
  originalAboutContent: data.aboutContent || "",
  originalFaqs: JSON.stringify(normalizeFaqs(data.faqs)),
});

// A coupon is identified by its offerId everywhere in this page (table key,
// edit, delete), so a store that still carries legacy duplicate documents for
// one offer must not render twice.
const sortByRating = (coupons) =>
  [...coupons].sort(
    (a, b) => (parseInt(b.rating) || 0) - (parseInt(a.rating) || 0)
  );

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const CouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [storeData, setStoreData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  // Kept apart from `loading`: that one blanks the page for a spinner, which is
  // the wrong response to saving a single row.
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [isEditingStore, setIsEditingStore] = useState(false);
  // Row selection for bulk delete, keyed by offerId (the id this page uses for
  // every coupon operation).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // null when no bulk delete is running; otherwise { done, total } so the
  // toolbar can show progress while the loop works through the selection.
  const [bulkProgress, setBulkProgress] = useState(null);
  const selectAllRef = useRef(null);

  const isBulkDeleting = bulkProgress !== null;
  const allSelected = coupons.length > 0 && selectedIds.size === coupons.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  // "Partially selected" is not an attribute React can set, so it is applied to
  // the DOM node directly.
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const fetchCouponsData = useCallback(async () => {
    if (!searchQuery) return;
    setLoading(true);
    try {
      const data = await fetchData(
        "/stores",
        `?id=${encodeURIComponent(searchQuery)}`
      );
      setStoreData(buildStoreState(data));
      setCoupons(sortByRating(data.coupons || []));
      // The rows just changed underneath the selection.
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Error fetching data:", error);
      alert(
        error?.status === 404
          ? `No store called "${searchQuery}". Check the id, or create the store first.`
          : error?.message || "Failed to load the store. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchCouponsData();
  }, [fetchCouponsData]);

  const updateStore = async () => {
    setLoading(true);
    try {
      const storeUpdateData = {};

      if (storeData.name !== storeData.originalName) {
        storeUpdateData.name = storeData.name;
      }
      if (storeData.description !== storeData.originalDescription) {
        storeUpdateData.description = storeData.description;
      }
      if (storeData.affiliateLink !== storeData.originalAffiliateLink) {
        storeUpdateData.affiliateLink = storeData.affiliateLink;
      }
      if (storeData.category !== storeData.originalCategory) {
        storeUpdateData.category = storeData.category;
      }
      if (storeData.metaKeywords !== storeData.originalMetaKeywords) {
        storeUpdateData.metaKeywords = storeData.metaKeywords;
      }
      if (storeData.metaDescription !== storeData.originalMetaDescription) {
        storeUpdateData.metaDescription = storeData.metaDescription;
      }
      if (storeData.aboutContent !== storeData.originalAboutContent) {
        storeUpdateData.aboutContent = storeData.aboutContent;
      }
      const cleanedFaqs = normalizeFaqs(storeData.faqs);
      if (JSON.stringify(cleanedFaqs) !== storeData.originalFaqs) {
        storeUpdateData.faqs = cleanedFaqs;
      }

      if (storeData.newLogo) {
        storeUpdateData.brandLogoUrlSquare = await readFileAsDataUrl(
          storeData.newLogo
        );
      }

      await sendUpdateRequest(storeUpdateData);
      setIsEditingStore(false);
      // Reload so the `original*` baselines match what the server now holds.
      await fetchCouponsData();
    } catch (error) {
      console.error("Error updating store:", error);
      alert("Error updating store.");
    } finally {
      setLoading(false);
    }
  };

  const sendUpdateRequest = async (data) => {
    await fetchData(
      "/stores/update",
      `?id=${encodeURIComponent(searchQuery)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
  };

  const handleEditClick = (coupon) => {
    setSelectedCoupon(coupon);
    setIsModalOpen(true);
  };

  const handleCancelEdit = () => {
    // Discard unsaved edits by restoring the baselines captured on load.
    setStoreData((prevData) => ({
      ...prevData,
      name: prevData.originalName,
      description: prevData.originalDescription,
      affiliateLink: prevData.originalAffiliateLink,
      category: prevData.originalCategory,
      metaKeywords: prevData.originalMetaKeywords,
      metaDescription: prevData.originalMetaDescription,
      brandLogoUrlSquare: prevData.originalBrandLogoUrlSquare,
      aboutContent: prevData.originalAboutContent,
      faqs: JSON.parse(prevData.originalFaqs || "[]"),
      newLogo: undefined,
    }));
    setIsEditingStore(false);
  };

  /**
   * An offer id must be digits only and unused in this store.
   *
   * Letters are how "1.0082E+12" and hand-typed values got in, and a repeated
   * id inside one store overwrites its twin on save, because the id is what
   * identifies a coupon within its store.
   */
  const validateOfferId = (candidate, replacing) => {
    const offerId = String(candidate.offerId ?? "").trim();
    if (!offerId) return "An offer ID is required.";
    if (!/^\d+$/.test(offerId)) {
      const expanded = expandScientific(offerId);
      if (expanded !== offerId) {
        return `Offer ID "${offerId}" is scientific notation — Excel shortened it. Enter the full number (it would become ${expanded}, which is not the original).`;
      }
      return `Offer ID "${offerId}" must be digits only — no letters, spaces or punctuation.`;
    }
    if (offerId.length > MAX_OFFER_ID_DIGITS) {
      return `Offer ID "${offerId}" is ${offerId.length} digits. The format is DDMMYYYY plus a sequence, at most ${MAX_OFFER_ID_DIGITS} digits (e.g. 300720260095).`;
    }
    const clash = coupons.find(
      (c) => c.offerId === offerId && c.offerId !== replacing?.offerId
    );
    if (clash) {
      return `Offer ID ${offerId} is already used in this store by "${clash.title || clash.couponCode}". Give this coupon a different id.`;
    }
    return null;
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCoupon(null);
  };

  const handleSaveCoupon = async (editedCoupon) => {
    const problem = validateOfferId(editedCoupon, selectedCoupon);
    if (problem) {
      alert(problem);
      return;
    }

    try {
      setSaving(true);
      // fetchData attaches the admin token; a bare fetch would get a 401 and
      // the global interceptor in src/index.js would log the admin out.
      await fetchData("/coupons/save", null, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editedCoupon),
      });

      // The saved coupon replaces its row in place. Re-fetching the store threw
      // the whole page away -- scroll position, the store panel, everything --
      // to show a change already known here.
      setCoupons((current) => {
        const previousId = selectedCoupon?.offerId;
        const exists = current.some((c) => c.offerId === previousId);
        const next = exists
          ? current.map((c) => (c.offerId === previousId ? { ...c, ...editedCoupon } : c))
          : [...current, editedCoupon];
        return sortByRating(next);
      });
      // An edit may have changed the offer id, which is what the selection is
      // keyed on -- follow the row to its new id.
      if (selectedCoupon?.offerId && selectedCoupon.offerId !== editedCoupon.offerId) {
        setSelectedIds((current) => {
          if (!current.has(selectedCoupon.offerId)) return current;
          const next = new Set(current);
          next.delete(selectedCoupon.offerId);
          next.add(editedCoupon.offerId);
          return next;
        });
      }
      setIsModalOpen(false);
      setSelectedCoupon(null);
    } catch (error) {
      console.error("Error saving coupon:", error);
      alert(error?.message || "Failed to save coupon. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleCouponSelected = (offerId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    // Anything short of "every row ticked" means the header box selects all;
    // only a full selection clears it.
    setSelectedIds(
      allSelected ? new Set() : new Set(coupons.map((c) => c.offerId))
    );
  };

  // The store scopes the delete: a few offer ids are reused across stores. A
  // coupon loaded without its own `store` belongs to the one being viewed.
  const deleteCouponRequest = (coupon) =>
    fetchData(
      "/coupons/delete",
      `?id=${encodeURIComponent(coupon.offerId)}&store=${encodeURIComponent(
        coupon.store || searchQuery || ""
      )}`,
      { method: "DELETE" }
    );

  const forgetSelection = (offerId) =>
    setSelectedIds((current) => {
      if (!current.has(offerId)) return current;
      const next = new Set(current);
      next.delete(offerId);
      return next;
    });

  const handleDeleteCoupon = async (coupon) => {
    const confirmed = window.confirm(
      `Delete coupon "${coupon.title || coupon.offerId}"? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      await deleteCouponRequest(coupon);
      setCoupons((current) => current.filter((c) => c.offerId !== coupon.offerId));
      forgetSelection(coupon.offerId);
    } catch (error) {
      console.error("Error deleting coupon:", error);
      alert(error?.message || "Failed to delete coupon. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Deletes the ticked coupons one request at a time, keeping the toolbar
   * showing how far it has got. Deleting is per-coupon on the API, so a failure
   * part-way through leaves the earlier deletes done -- the rows that did go are
   * removed, the ones that failed stay ticked so the button can be pressed
   * again for just those.
   */
  const handleDeleteSelected = async () => {
    const targets = coupons.filter((c) => selectedIds.has(c.offerId));
    if (targets.length === 0) return;

    const confirmed = window.confirm(
      `Delete ${targets.length} selected coupon${targets.length === 1 ? "" : "s"
      } from "${searchQuery}"? This cannot be undone.`
    );
    if (!confirmed) return;

    const deleted = [];
    const failed = [];
    setBulkProgress({ done: 0, total: targets.length });
    try {
      for (const coupon of targets) {
        try {
          await deleteCouponRequest(coupon);
          deleted.push(coupon.offerId);
        } catch (error) {
          console.error(`Error deleting coupon ${coupon.offerId}:`, error);
          failed.push(coupon);
        }
        setBulkProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }

      const removed = new Set(deleted);
      setCoupons((current) => current.filter((c) => !removed.has(c.offerId)));
      setSelectedIds(new Set(failed.map((c) => c.offerId)));
    } finally {
      setBulkProgress(null);
    }

    if (failed.length > 0) {
      alert(
        `Deleted ${deleted.length} of ${targets.length}. ` +
        `These could not be deleted and are still selected:\n\n` +
        failed
          .slice(0, 10)
          .map((c) => `- ${c.offerId} ${c.title || ""}`.trim())
          .join("\n") +
        (failed.length > 10 ? `\n...and ${failed.length - 10} more` : "")
      );
    }
  };

  /**
   * Empties the store of coupons but keeps the store itself. Typing the store
   * id is deliberate friction: this cannot be undone from here, and a stray
   * click on a store with hundreds of coupons is expensive to recover from.
   */
  const handleDeleteAllCoupons = async () => {
    const storeId = searchQuery;
    const typed = window.prompt(
      `Delete ALL ${coupons.length} coupons from "${storeId}"?

` +
        `The store itself will be kept. This cannot be undone.

` +
        `Type the store id to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== storeId) {
      alert("That did not match the store id — nothing was deleted.");
      return;
    }

    try {
      setLoading(true);
      const result = await fetchData(
        "/coupons/deleteAll",
        `?store=${encodeURIComponent(storeId)}`,
        { method: "DELETE" }
      );
      alert(result?.message || "Coupons deleted.");
      await fetchCouponsData();
    } catch (error) {
      console.error("Error deleting all coupons:", error);
      alert(error?.message || "Failed to delete the coupons. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /** Removes the store and everything in it, including its coupons. */
  const handleDeleteStore = async () => {
    const storeId = searchQuery;
    const typed = window.prompt(
      `Delete the store "${storeId}" and all ${coupons.length} of its coupons?

` +
        `The store, its coupons and its search entry are all removed. ` +
        `This cannot be undone.

` +
        `Type the store id to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== storeId) {
      alert("That did not match the store id — nothing was deleted.");
      return;
    }

    try {
      setLoading(true);
      const result = await fetchData(
        "/stores/delete",
        `?id=${encodeURIComponent(storeId)}`,
        { method: "DELETE" }
      );
      alert(result?.message || "Store deleted.");
      setCoupons([]);
      setSelectedIds(new Set());
      setStoreData(buildStoreState({}));
      setSearchQuery("");
      setSearchTerm("");
    } catch (error) {
      console.error("Error deleting store:", error);
      alert(error?.message || "Failed to delete the store. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStoreFieldChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "brandLogo" && files.length > 0) {
      const logoFile = files[0];
      setStoreData((prevData) => ({
        ...prevData,
        newLogo: logoFile,
        brandLogoUrlSquare: URL.createObjectURL(logoFile),
      }));
    } else {
      setStoreData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFaqChange = (index, field, value) => {
    setStoreData((prevData) => {
      const faqs = [...(prevData.faqs || [])];
      faqs[index] = { ...faqs[index], [field]: value };
      return { ...prevData, faqs };
    });
  };

  const addFaq = () => {
    setStoreData((prevData) => ({
      ...prevData,
      faqs: [...(prevData.faqs || []), { question: "", answer: "" }],
    }));
  };

  const removeFaq = (index) => {
    setStoreData((prevData) => ({
      ...prevData,
      faqs: (prevData.faqs || []).filter((_, i) => i !== index),
    }));
  };

  // Every coupon is rendered. The store page returns the whole set in one
  // document, so paging five at a time only hid rows behind clicks.


  return (
    <>
      {loading && <Spinner />}
      <div className="flex flex-col bg-gray-100 w-full min-h-screen p-4 md:p-6 space-y-6 md:space-y-8">
        <header className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4 text-center md:text-left">Stores</h1>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center w-full md:w-auto gap-2 md:gap-0">
              <input
                type="text"
                placeholder="Search store..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="p-3 border border-gray-300 rounded-lg md:rounded-l-lg md:rounded-r-none w-full md:max-w-xs"
              />
              <button
                onClick={() => setSearchQuery(searchTerm)}
                className="p-3 bg-red-600 text-white rounded-lg md:rounded-l-none md:rounded-r-lg w-full md:w-auto"
              >
                Search
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={fetchCouponsData}
                className="p-3 bg-green-500 text-white rounded-lg w-full md:w-auto shadow-md hover:bg-green-600 transition-transform transform hover:scale-105"
              >
                Refresh
              </button>
              {storeData.name && (
                <>
                  <button
                    onClick={handleDeleteAllCoupons}
                    disabled={coupons.length === 0 || isBulkDeleting}
                    title={
                      coupons.length === 0
                        ? "This store has no coupons"
                        : `Remove all ${coupons.length} coupons but keep the store`
                    }
                    className="p-3 bg-orange-600 text-white rounded-lg w-full md:w-auto shadow-md hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                    Delete all coupons
                  </button>
                  <button
                    onClick={handleDeleteStore}
                    disabled={isBulkDeleting}
                    title="Remove the store and every coupon in it"
                    className="p-3 bg-red-700 text-white rounded-lg w-full md:w-auto shadow-md hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                    Delete store
                  </button>
                </>
              )}
            </div>
          </div>
        </header>
        {storeData.name && !loading && (
          <div className="bg-white p-4 md:p-8 rounded-lg shadow-xl mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between mb-6 relative gap-8 md:gap-0">
              <div className="flex flex-col md:flex-row items-center relative w-full">
                <div className="relative mb-6 md:mb-0 text-center">
                  <img
                    src={storeData.brandLogoUrlSquare}
                    alt={storeData.name}
                    className="w-24 h-24 md:w-28 md:h-28 rounded-full md:mr-6 shadow-md inline-block"
                  />
                  {isEditingStore && (
                    <label className="absolute bottom-0 right-0 md:bottom-2 md:right-2 cursor-pointer">
                      <input
                        type="file"
                        name="brandLogo"
                        accept="image/*"
                        onChange={handleStoreFieldChange}
                        className="hidden"
                      />
                      <div className="flex items-center justify-center bg-gray-800 rounded-full p-2">
                        <FontAwesomeIcon
                          icon={faEdit}
                          className="text-white text-xs"
                        />
                      </div>
                    </label>
                  )}
                </div>
                <div className="md:ml-6 text-center md:text-left w-full">
                  {isEditingStore ? (
                    <>
                      <input
                        type="text"
                        name="name"
                        value={storeData.name}
                        onChange={handleStoreFieldChange}
                        className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 w-full text-center md:text-left"
                      />
                      <label className="block text-sm text-gray-600 mb-1 text-left">
                        Category — the store's coupons are listed under this
                      </label>
                      <select
                        name="category"
                        value={storeData.category || ""}
                        onChange={handleStoreFieldChange}
                        className="w-full p-2 border border-gray-300 rounded mb-2 bg-white"
                      >
                        <option value="">— choose a category —</option>
                        {CATEGORIES.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <textarea
                        name="description"
                        value={storeData.description}
                        onChange={handleStoreFieldChange}
                        className="w-full p-2 border border-gray-300 rounded mb-2"
                      />
                      <input
                        type="text"
                        name="affiliateLink"
                        value={storeData.affiliateLink}
                        onChange={handleStoreFieldChange}
                        className="w-full p-2 border border-gray-300 rounded mb-2"
                      />
                      <input
                        type="text"
                        name="metaKeywords"
                        value={storeData.metaKeywords || ""}
                        onChange={handleStoreFieldChange}
                        className="w-full p-2 border border-gray-300 rounded mb-2"
                        placeholder="Meta Keywords"
                      />
                      <textarea
                        name="metaDescription"
                        value={storeData.metaDescription || ""}
                        onChange={handleStoreFieldChange}
                        className="w-full p-2 border border-gray-300 rounded mb-2"
                        placeholder="Meta Description"
                      />
                      <label className="block text-left text-sm font-semibold text-gray-700 mt-3 mb-1">
                        About content (blank line separates paragraphs)
                      </label>
                      <textarea
                        name="aboutContent"
                        value={storeData.aboutContent || ""}
                        onChange={handleStoreFieldChange}
                        rows={6}
                        className="w-full p-2 border border-gray-300 rounded mb-2"
                        placeholder="About the store — used in the 'About {store}' section of the coupon page."
                      />
                      <div className="mt-4 text-left">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700">
                            FAQs
                          </span>
                          <button
                            type="button"
                            onClick={addFaq}
                            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                          >
                            + Add FAQ
                          </button>
                        </div>
                        {(storeData.faqs || []).length === 0 && (
                          <p className="text-xs text-gray-400 mb-2">
                            No FAQs yet. Add a few Q&amp;A pairs to show the FAQ
                            accordion on the coupon page.
                          </p>
                        )}
                        {(storeData.faqs || []).map((faq, i) => (
                          <div
                            key={i}
                            className="border border-gray-200 rounded p-3 mb-2 bg-gray-50"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-500">
                                FAQ #{i + 1}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeFaq(i)}
                                className="text-red-500 hover:text-red-700 text-xs"
                              >
                                Remove
                              </button>
                            </div>
                            <input
                              type="text"
                              value={faq.question || ""}
                              onChange={(e) =>
                                handleFaqChange(i, "question", e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded mb-2 text-sm"
                              placeholder="Question"
                            />
                            <textarea
                              value={faq.answer || ""}
                              onChange={(e) =>
                                handleFaqChange(i, "answer", e.target.value)
                              }
                              rows={2}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              placeholder="Answer"
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                        {storeData.name}
                      </h2>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        {storeData.category || "no category set"}
                      </p>
                      <p className="text-gray-600 text-sm md:text-base">{storeData.description}</p>
                      <a
                        href={storeData.affiliateLink || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-white bg-red-500 hover:bg-red-600 transition-colors rounded-md py-2 px-4 inline-block mt-2 text-sm md:text-base ${!storeData.affiliateLink &&
                          "opacity-50 cursor-not-allowed"
                          }`}
                        onClick={(e) =>
                          !storeData.affiliateLink && e.preventDefault()
                        }
                      >
                        Visit Affiliate Link
                      </a>
                    </>
                  )}
                </div>
              </div>
              <div className="w-full md:w-auto flex justify-center md:justify-end">
                {isEditingStore ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={updateStore}
                      className="p-2 bg-green-500 text-white rounded hover:bg-green-600 transition text-sm md:text-base"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition text-sm md:text-base"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsEditingStore(true)}
                    className="p-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-sm md:text-base"
                  >
                    Edit Store
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={coupons.length === 0 || isBulkDeleting}
                  className="form-checkbox h-4 w-4 text-red-600"
                />
                {allSelected ? "Deselect all" : "Select all"}
              </label>
              <span className="text-sm text-gray-500">
                {selectedIds.size} of {coupons.length} selected
              </span>
              <button
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0 || isBulkDeleting || saving}
                title={
                  selectedIds.size === 0
                    ? "Tick the coupons you want to delete"
                    : `Delete the ${selectedIds.size} selected coupons`
                }
                className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed md:ml-auto"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                {isBulkDeleting
                  ? `Deleting ${bulkProgress.done} of ${bulkProgress.total}...`
                  : `Delete selected${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
              </button>
            </div>
            {isBulkDeleting && (
              <div className="mb-3 h-2 w-full bg-gray-200 rounded overflow-hidden">
                <div
                  className="h-full bg-red-600 transition-all duration-200"
                  style={{
                    width: `${Math.round(
                      (bulkProgress.done / bulkProgress.total) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left text-gray-700">
                <thead className="text-xs text-gray-600 uppercase bg-gray-100">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={coupons.length === 0 || isBulkDeleting}
                        aria-label="Select all coupons"
                        className="form-checkbox h-4 w-4 text-red-600"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Offer ID
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Coupon Code
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Rating
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon, index) => (
                    <tr
                      key={coupon.offerId}
                      className={`${selectedIds.has(coupon.offerId)
                        ? "bg-red-50"
                        : index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-100"
                        } hover:bg-gray-200 transition-colors duration-200`}
                    >
                      <td className="px-6 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(coupon.offerId)}
                          onChange={() => toggleCouponSelected(coupon.offerId)}
                          disabled={isBulkDeleting}
                          aria-label={`Select coupon ${coupon.offerId}`}
                          className="form-checkbox h-4 w-4 text-red-600"
                        />
                      </td>
                      <td className="px-6 py-4 text-gray-800 font-medium">
                        {coupon.offerId}
                      </td>
                      <td className="px-6 py-4 text-gray-800">
                        {coupon.title}
                      </td>
                      <td className="px-6 py-4 text-gray-800">
                        {coupon.couponCode}
                      </td>
                      <td className="px-6 py-4 text-gray-800">
                        {coupon.description}
                      </td>
                      <td className="px-6 py-4 text-center text-gray-800">
                        {coupon.rating}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <button
                            disabled={saving || isBulkDeleting}
                            onClick={() => handleEditClick(coupon)}
                            className="text-red-600 hover:text-red-800 flex items-center"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-2" />
                            Edit
                          </button>
                          <button
                            disabled={saving || isBulkDeleting}
                            onClick={() => handleDeleteCoupon(coupon)}
                            className="text-gray-600 hover:text-red-700 flex items-center"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-2" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}
      </div>
      {selectedCoupon && (
        <CouponModal
          coupon={selectedCoupon}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveCoupon}
        />
      )}
    </>
  );
};

export default CouponsPage;
